import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import { useMemo } from 'react'
import MultiImageUploader from "../../../../common/MultiImageUploader";
import { useRoomsData } from "../../../../common/hooks/useRoomsData";
import { useRoomPicturesData } from "../../../../common/hooks/useRoomPicturesData";
import { useEditableArray } from "../../../../common/hooks/useEditableArray";
import { withMultipleQueryData } from "../../../../common/withQueryData";
import { OrdinaryButton } from "../../../../common/Buttons";
import { useFormStatus } from "../../../../common/inputs/useFormStatus";
import { useAlertNotification } from "../../../../common/hooks/useAlertNotification";

/**
 * Extracts a picture id (filename without extension) from a URL.
 */
const pictureIdFromUrl = (url) =>
    url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));

const RoomsPicturesEditor = ({ propertyId, roomId, onClose }) => {
    const { queryResults } = useRoomsData(propertyId, roomId);
    const { picturesQueryData, uploadPictures, deletePictures, reorderPictures } = useRoomPicturesData(propertyId, roomId);

    const onSubmit = async (pictures) => {
        const currentUrls = picturesQueryData.data?.pictureUrls || [];
        const currentPictures = currentUrls.map(url => ({ id: pictureIdFromUrl(url), url }));

        const picturesToDelete = currentPictures
            .filter(p => !pictures.some(up => up.id === p.id))
            .map(p => p.id);
        const picturesToAdd = pictures.filter(p => !!p.file);

        if (picturesToDelete.length) {
            await deletePictures(picturesToDelete);
        }

        if (picturesToAdd.length) {
            const updatedAlbum = await uploadPictures(picturesToAdd.map(p => p.file));
            // Map server-assigned IDs back onto the pictures array so the reorder call uses correct IDs.
            if (updatedAlbum?.urls) {
                const uploadedUrls = updatedAlbum.urls;
                for (let i = 0; i < picturesToAdd.length; i++) {
                    const newUrl = uploadedUrls[uploadedUrls.length - picturesToAdd.length + i];
                    const newId = pictureIdFromUrl(newUrl);
                    const slot = pictures.find(p => p.id === picturesToAdd[i].id);
                    if (slot) slot.id = newId;
                }
            }
        }

        await reorderPictures(pictures.map(p => p.id));
    };

    return <Dialog open fullWidth maxWidth="500px">
        <RooomPicturesEditorContentWithMultipleQueryData
            queryResults={{
                room: queryResults,
                roomPictures: picturesQueryData
            }}
            onClose={onClose}
            onSubmit={onSubmit}
        />
    </Dialog>
}

const RoomPicturesEditorContent = ({ queryResults, reloadAll, onClose, onSubmit }) => {
    const [status, setStatus, statusCodes] = useFormStatus();
    const { room: roomQueryResults, roomPictures: roomPicturesQueryResults } = queryResults;
    const { setAlert } = useAlertNotification();

    /* eslint-disable no-unused-vars */
    const { data: room } = roomQueryResults;
    const { data: picturesAlbum } = roomPicturesQueryResults;
    /* eslint-enable no-unused-vars */

    const existingPictures = useMemo(() => (picturesAlbum?.pictureUrls || []).map(url => ({
        id: pictureIdFromUrl(url),
        url,
    })), [picturesAlbum]);

    const [editablePictures, setEditablePictures, isChanged] = useEditableArray(existingPictures);

    const _onSubmit = async (updatedPictures) => {
        setStatus(statusCodes.SAVING);
        try {
            await onSubmit(updatedPictures);
            setAlert({
                message: "Successfully updated room pictures",
                severity: 'success'
            });
            setStatus(statusCodes.AWAITING_USER_INPUT);
        } catch (error) {
            console.error(error);
            setAlert({
                message: "Failed to update room pictures - see server logs for more details",
                severity: 'error'
            });
            setStatus(statusCodes.AWAITING_USER_INPUT);
        }
    };

    return <>
        <DialogTitle>Editing pictures of room: {room.name}</DialogTitle>
        <DialogContent>
            <MultiImageUploader images={editablePictures} onChange={setEditablePictures} />
        </DialogContent>
        <DialogActions>
            <OrdinaryButton onClick={onClose} text='Close' />
            <OrdinaryButton
                onClick={() => _onSubmit(editablePictures)}
                disabled={!isChanged || status !== statusCodes.AWAITING_USER_INPUT}
                loading={status === statusCodes.SAVING}
                text='Update'
            />
        </DialogActions>
    </>;
}

const RooomPicturesEditorContentWithMultipleQueryData = withMultipleQueryData(RoomPicturesEditorContent);

export default RoomsPicturesEditor;
